import type { CSSProperties } from "react";
import type { ResolvedPadding } from "../shared/types";
import {
  extractPaddingValues,
  extractPlainText,
  parseNumericStyle,
} from "../shared/utils";

export const VML_BUTTON_DIMENSIONS = {
  TEXT_WIDTH_MULTIPLIER: 0.5,
  MIN_WIDTH_MARGIN: 0,
  WIDTH_MARGIN_SCALE: 0.15,
  MIN_HEIGHT_MARGIN: 6,
  HEIGHT_MARGIN_SCALE: 0.4,
} as const;

export const VML_BORDER_RADIUS = {
  MAX_ARCSIZE_PERCENT: 50,
} as const;

export const LINE_HEIGHT = {
  UNITLESS_THRESHOLD: 10,
  DEFAULT_MULTIPLIER: 1.2,
} as const;

export const FONT = {
  DEFAULT_SIZE: 16,
} as const;

export const calculateAverageBorderRadius = (styles: CSSProperties): number => {
  if (styles.borderRadius != null) {
    return parseNumericStyle(styles.borderRadius);
  }

  const topLeft = parseNumericStyle(styles.borderTopLeftRadius);
  const topRight = parseNumericStyle(styles.borderTopRightRadius);
  const bottomLeft = parseNumericStyle(styles.borderBottomLeftRadius);
  const bottomRight = parseNumericStyle(styles.borderBottomRightRadius);

  return Math.round((topLeft + topRight + bottomLeft + bottomRight) / 4);
};

export const normalizeLineHeight = (
  lineHeight: unknown,
  fontSize: number,
): number => {
  const parsedLineHeight = parseNumericStyle(lineHeight, 0);

  if (parsedLineHeight === 0) {
    return fontSize * LINE_HEIGHT.DEFAULT_MULTIPLIER;
  }

  if (parsedLineHeight < LINE_HEIGHT.UNITLESS_THRESHOLD) {
    return parsedLineHeight * fontSize;
  }

  return parsedLineHeight;
};

export const calculateButtonWidth = (
  text: unknown,
  fontSize: number,
  padding: ResolvedPadding,
): number => {
  const plainText = extractPlainText(text);
  const estimatedTextWidth = Math.ceil(
    plainText.length * fontSize * VML_BUTTON_DIMENSIONS.TEXT_WIDTH_MULTIPLIER,
  );

  const { left, right } = extractPaddingValues(padding);

  const extraMargin = Math.max(
    VML_BUTTON_DIMENSIONS.MIN_WIDTH_MARGIN,
    fontSize * VML_BUTTON_DIMENSIONS.WIDTH_MARGIN_SCALE,
  );

  return left + estimatedTextWidth + right + extraMargin;
};

export const calculateButtonHeight = (
  padding: ResolvedPadding,
  lineHeight: number,
  fontSize: number,
): number => {
  const { top, bottom } = extractPaddingValues(padding);

  const textHeight = Math.max(lineHeight, fontSize);

  const extraMargin = Math.max(
    VML_BUTTON_DIMENSIONS.MIN_HEIGHT_MARGIN,
    fontSize * VML_BUTTON_DIMENSIONS.HEIGHT_MARGIN_SCALE,
  );

  return top + textHeight + bottom + extraMargin;
};

export const calculateArcsize = (
  borderRadius: number,
  height: number,
): number => {
  if (borderRadius === 0 || height === 0) return 0;

  const arcsize = Math.round(((borderRadius * 2) / height) * 100);

  return Math.min(arcsize, 100);
};
